import React from 'react'
import PropTypes from 'prop-types'
import { render } from 'react-dom'
import GoogleMapReact from 'google-map-react'
import { fitBounds } from 'google-map-react/utils'
import { Marker } from './partials'
import * as Component from './Map.styles'
import { useMobileSwitch, useMapPrivacyPolicy, useScrollLock } from 'hooks'

const Map = (props) => {
  const {
    defaultCenter,
    defaultZoom,
    radius,
    markers,
    isMapFullscreen,
    onMarkerClicked,
    onMarkerHovered,
    onToggleFullscreen,
    onLoad,
    height,
    mapPosition,
    showAlphanumericMarker,
    apiKey,
    ...other
  } = props

  const { wantGoogleMaps, MapPrivacyPolicy } = useMapPrivacyPolicy()

  const [map, setMap] = React.useState(null)
  const [maps, setMaps] = React.useState(null)
  const [showInfoBox, setShowInfoBox] = React.useState(false)
  const [radiusCircles, setRadiusCircles] = React.useState({
    outerCircle: null,
  })
  const [center, setCenter] = React.useState(defaultCenter)
  const [zoom, setZoom] = React.useState(defaultZoom)

  const isMobile = useMobileSwitch()
  useScrollLock(isMobile && isMapFullscreen)

  const fullscreenButtonRef = React.useRef()

  const fullscreenButton = useMapControl(
    <Component.FullscreenButton
      ref={ fullscreenButtonRef }
      id='map-fullscreen-btn'
      data-cy='map-fullscreen-btn'
      onClick={ () => toggleMapFullscreen(!window.isFullscreen) }>
      <Component.FullscreenButtonIcon
        className={ isMapFullscreen ? 'icon-f-shrink' : 'icon-f-enlarge' }
      />
      <span className='sr-only'>
        {isMapFullscreen ? 'Karte verkleinern' : 'Karte vergrößern'}
      </span>
    </Component.FullscreenButton>
  )

  React.useEffect(() => {
    window.isFullscreen = isMapFullscreen
  }, [isMapFullscreen])

  React.useEffect(() => {
    if (radiusCircles.outerCircle) {
      if (radius) {
        radiusCircles.outerCircle.setRadius(radius)
        radiusCircles.outerCircle.setCenter(defaultCenter)
        radiusCircles.outerCircle.setMap(map)
      } else {
        radiusCircles.outerCircle.setMap(null)
      }
    }
  }, [radius, radiusCircles, map, defaultCenter])

  React.useEffect(() => {
    if (maps && markers) {
      const bounds = new maps.LatLngBounds()

      if (markers.length === 0) return

      markers.forEach((marker) => {
        if (!marker.lat || !marker.lng) {
          return
        }

        bounds.extend(new maps.LatLng(marker.lat, marker.lng))
      })

      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01
        )
        const extendPoint2 = new maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01
        )
        bounds.extend(extendPoint1)
        bounds.extend(extendPoint2)
      }

      // check if methods exists will be undefined if map is not initialized correctly
      if (!bounds.getNorthEast() || !bounds.getSouthWest()) return

      try {
        const newBounds = {
          ne: {
            lat: bounds.getNorthEast().lat(),
            lng: bounds.getNorthEast().lng(),
          },
          sw: {
            lat: bounds.getSouthWest().lat(),
            lng: bounds.getSouthWest().lng(),
          },
        }

        const size = {
          width: 390,
          height: 390,
        }

        let center, zoom

        // set center and zoom to only marker position
        if (markers.length === 1 && markers[0]) {
          center = markers[0]
          zoom = 13
        } else {
          const calcBounds = fitBounds(newBounds, size)

          center = calcBounds.center
          zoom = calcBounds.zoom
        }

        setCenter(center)
        setZoom(zoom)
      } catch (e) {
        console.log('error: ', e)
      }
    }
  }, [markers, maps, defaultCenter])

  const createMapOptions = () => ({
    fullscreenControl: false,
  })

  const handleMarkerClicked = (index, markerId) => {
    onMarkerClicked(index, markerId)
    if (isMapFullscreen) {
      setShowInfoBox(index)
      const activeMarker = markers[index]
      map.panTo(new maps.LatLng(activeMarker.lat, activeMarker.lng))
    }
  }

  /**
   * set style class for fullscreen/non-fullscreen map
   * @param bool
   */
  const toggleMapFullscreen = (bool) => {
    window.isFullscreen = bool
    onToggleFullscreen && onToggleFullscreen(bool)

    if (bool) {
      fullscreenButtonRef.current.classList.remove('icon-f-enlarge')
      fullscreenButtonRef.current.classList.add('icon-f-shrink')
    } else {
      setShowInfoBox(null)
      fullscreenButtonRef.current.classList.remove('icon-f-shrink')
      fullscreenButtonRef.current.classList.add('icon-f-enlarge')
    }
  }

  return (
    <Component.Root
      height={ height }
      isFullscreen={ isMapFullscreen }
      mapPosition={ mapPosition }
      isMobile={ isMobile }
      { ...other }>
      {!wantGoogleMaps ? (
        <Component.PrivacyPolicyWrapper>
          <MapPrivacyPolicy />
        </Component.PrivacyPolicyWrapper>
      ) : (
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{
            key: apiKey,
          }}
          defaultZoom={ defaultZoom }
          center={ center }
          zoom={ zoom }
          options={ createMapOptions }
          onGoogleApiLoaded={ ({ map, maps }) => {
            onLoad?.()
            fullscreenButton.initControl(map, maps.ControlPosition.TOP_RIGHT)
            setMap(map)
            setMaps(maps)
            if (radius) {
              setRadiusCircles({
                outerCircle: new maps.Circle({
                  strokeColor: 'rgba(255, 153, 0, 0.3)',
                  strokeWeight: 2,
                  strokeOpacity: 1,
                  fillColor: 'rgba(255, 153, 0, 0.05)',
                  fillOpacity: 1,
                  map,
                  center: defaultCenter,
                  radius,
                }),
              })
            }
          } }>
          {radius && defaultCenter && (
            <Component.CenterMarker
              lat={ defaultCenter.lat }
              lng={ defaultCenter.lng }
            />
          )}
          {markers &&
            markers.map((marker, index) => (
              <Marker
                { ...marker }
                key={ index }
                id={ `marker-${ index }` }
                showInfoBox={ showInfoBox === index }
                handleMarkerClick={ () => handleMarkerClicked(index, marker.id) }
                handleMarkerHover={ (hover) =>
                  onMarkerHovered(hover, index, marker.id)
                }
                handleCloseButton={ () => setShowInfoBox(false) }
                showAlphanumericMarker={ showAlphanumericMarker }
              />
            ))}
        </GoogleMapReact>
      )}
    </Component.Root>
  )
}

const useMapControl = (children) => {
  const initControl = (map, controlPosition) => {
    if (map && controlPosition) {
      render(
        <div ref={ (el) => map.controls[controlPosition].push(el) }>
          {children}
        </div>,
        document.createElement('div')
      )
    }
  }

  return {
    initControl,
  }
}

Map.defaultProps = {
  defaultZoom: 1,
  isMapFullscreen: false,
  radius: false,
  showAlphanumericMarker: false,
}

Map.propTypes = {
  /** Map center (used to draw search center and radius) */
  defaultCenter: PropTypes.exact({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  /** Map zoom */
  defaultZoom: PropTypes.number,
  /** In meters */
  // radius: PropTypes.number.isRequired,
  radius: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      active: PropTypes.bool,
      title: PropTypes.string.isRequired,
      street: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      phone: PropTypes.string,
    })
  ),
  /** defines if map is drawn big or small */
  isMapFullscreen: PropTypes.bool,
  /**
   * Fired when marker was clicked
   * Signature: function(index: Number) {...}
   */
  onMarkerClicked: PropTypes.func,
  /**
   * Fired when marker was hovered
   * Signature: function(hover: Boolean, index: Number) {...}
   */
  onMarkerHovered: PropTypes.func,
  /**
   * Fired when toggle button was clicked
   * Signature: function(toggle: Bool) {...}
   */
  onToggleFullscreen: PropTypes.func,
  /**
   * Fired when google object is loaded
   * Signature: function() {...}
   */
  onLoad: PropTypes.func,
  /** largeMap is not equal on results and detailView **/
  height: PropTypes.string.isRequired,
  /** map position to control true fullscreen on detailView **/
  mapPosition: PropTypes.oneOf(['resultList', 'detailView']).isRequired,
  /** render alphanumeric icons instead of map icon **/
  showAlphanumericMarker: PropTypes.bool,
  /** render alphanumeric icons instead of map icon **/
  apiKey: PropTypes.string.isRequired,
}

export default React.memo(Map, areEqual)

/**
 * compare props to avoid unnecessary re renderings
 * @param prevProps
 * @param nextProps
 */
function areEqual(prevProps, nextProps) {
  return (
    JSON.stringify(prevProps.markers) === JSON.stringify(nextProps.markers) &&
    prevProps.radius === nextProps.radius &&
    prevProps.defaultCenter === nextProps.defaultCenter
  )
}
